import { FormEvent, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '~/components/ui/drawer';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { ChatAction, ChatRequestBody, ChatTool } from '~/lib/chat';
import { useMediaQuery } from '@mui/material';
import { Pencil1Icon, SlashIcon, TrashIcon } from '@radix-ui/react-icons';
import { Button } from '~/components/ui/Button';
import FunctionCallingForm from './FunctionCallingForm';

export interface FunctionCallingSpecProps {
  requestBody: ChatRequestBody;
  setRequestBody: React.Dispatch<ChatAction<keyof ChatRequestBody>>;
}

export function FunctionCallingSpec({ requestBody, setRequestBody }: FunctionCallingSpecProps) {
  const [currentEditingFunction, setCurrentEditingFunction] = useState<ChatTool | null>(null);
  const [open, setOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const functionData = JSON.parse(`{"type": "function", "function": ${jsonInput}}`) as ChatTool;

      if (currentEditingFunction) {
        const index = requestBody.tools?.findIndex(
          (tool) => tool.function.name === currentEditingFunction.function.name,
        );
        if (index !== undefined && index !== -1) {
          requestBody.tools?.splice(index, 1);
        }
      } else {
        requestBody.tools = requestBody.tools || [];
      }
      requestBody.tools?.push(functionData);
      setRequestBody({ field: 'tools', value: requestBody.tools });

      setOpen(false);
      setJsonInput('');
      setCurrentEditingFunction(null);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert('Invalid JSON input');
      console.error('Invalid JSON input', e);
    }
  };
  const handleCancel = () => {
    setOpen(false);
    setJsonInput('');
    setCurrentEditingFunction(null);
  };

  const handleEdit = (func: ChatTool) => {
    setCurrentEditingFunction(func);
    setJsonInput(JSON.stringify(func.function, null, 2));
    setOpen(true);
  };

  const handleDelete = (functionToDelete: string) => {
    const index = requestBody.tools?.findIndex((tool) => tool.function.name === functionToDelete);
    if (index !== undefined && index !== -1) {
      requestBody.tools?.splice(index, 1);
    }
    if (open) {
      setOpen(false);
      setCurrentEditingFunction(null);
    }
  };

  return (
    <div className="w-full">
      <Table className="table-compact table w-full">
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(requestBody.tools?.length || 0) === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="py-4 text-left">
                No functions currently added
              </TableCell>
            </TableRow>
          ) : (
            requestBody.tools?.map((tool) => (
              <TableRow key={`function-def-${tool.function.name}`}>
                <TableCell className="min-w-[2rem] max-w-[8rem] font-medium">{tool.function.name}</TableCell>
                <TableCell className="min-w-8 max-w-8 text-right font-medium">
                  <Button variant="ghost" onClick={() => handleEdit(tool)}>
                    <Pencil1Icon className="h-4 w-4" />
                    <SlashIcon className="mx-0 h-4 w-4 px-0" />
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {isDesktop ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild className="hidden md:block">
            <Button variant="outline">Add Function</Button>
          </DialogTrigger>
          <DialogContent className="w-[calc(100dvw/2)] sm:max-w-sm md:max-w-md lg:max-w-lg">
            <DialogHeader>
              <DialogTitle>Function Schema</DialogTitle>
              <DialogDescription>Add JSON schema for a function below. Click save when you're done.</DialogDescription>
            </DialogHeader>

            <FunctionCallingForm
              jsonInput={jsonInput}
              setJsonInput={setJsonInput}
              handleSubmit={handleSubmit}
              cancel={handleCancel}
              currentEditingFunction={currentEditingFunction}
              handleDelete={handleDelete}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild className="md:hidden">
            <Button variant="outline">Add Function</Button>
          </DrawerTrigger>
          <DrawerContent className="md:hidden">
            <DrawerHeader className="text-left">
              <DrawerTitle>Function Schema</DrawerTitle>
              <DrawerDescription>Add JSON schema for a function below. Click save when you're done.</DrawerDescription>
            </DrawerHeader>

            <div className="mx-4 mb-4">
              <FunctionCallingForm
                jsonInput={jsonInput}
                cancel={handleCancel}
                setJsonInput={setJsonInput}
                handleSubmit={handleSubmit}
                currentEditingFunction={currentEditingFunction}
                handleDelete={handleDelete}
              />
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}
